import { useEffect, useLayoutEffect, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button, StandardLayout } from '@factorialco/f0-react'
import { OneModal, Tabs } from '@factorialco/f0-react/dist/experimental'
import {
  ProjectManagementValueObjectsStatusTypesEnum,
  SubprojectSidepanelDetailsDocument,
  SubprojectsDataCollectionDocument,
  UpdateSubprojectDocument,
  UpdateSubprojectMutation,
} from 'generated/resources/graphql'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import { Result } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import SubprojectsPanel from 'modules/project_management/components/tables/SubprojectsDataCollection/SubprojectsPanel'
import SubprojectsSettingsTab from 'modules/project_management/components/tables/SubprojectsDataCollection/SubprojectsSettingsTab'
import { SubprojectBasicDetails } from 'modules/project_management/components/tables/SubprojectsDataCollection/types'
import { ISubprojectsPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/types'

export type { SubprojectBasicDetails }

export interface SubprojectSidepanelProps {
  subprojectId: number
  subprojectData: ISubprojectsPanelData
  onClose: () => void
  showSubprojectsSidepanelOne: boolean
  projectId: number
  projectCurrency: string
  canSeeLaborCosts: boolean
  subprojectEntityFeatureEnabled: boolean
}

const TABSIDS = {
  HOURS: 'hours',
  SETTINGS: 'settings',
} as const

const isValidStatus = (status: string): status is ProjectManagementValueObjectsStatusTypesEnum => {
  return ['active', 'closed', 'draft', 'processing'].includes(status)
}

const toStatusEnum = (status?: string | null): ProjectManagementValueObjectsStatusTypesEnum => {
  if (status && isValidStatus(status)) {
    return status
  }
  return 'active'
}

export const SubprojectsSidepanelTabs = ({
  subprojectId: subprojectId,
  subprojectData,
  onClose,
  showSubprojectsSidepanelOne,
  projectId,
  projectCurrency,
  canSeeLaborCosts,
  subprojectEntityFeatureEnabled,
}: SubprojectSidepanelProps) => {
  const [activeTab, setActiveTab] = useState<(typeof TABSIDS)[keyof typeof TABSIDS]>(
    subprojectId !== 0 ? TABSIDS.SETTINGS : TABSIDS.HOURS
  )
  useLayoutEffect(() => {
    setActiveTab(subprojectId !== 0 ? TABSIDS.SETTINGS : TABSIDS.HOURS)
  }, [showSubprojectsSidepanelOne, subprojectId])

  const { loading, data } = useQuery(SubprojectSidepanelDetailsDocument, {
    variables: {
      subprojectId: [subprojectId],
    },
    fetchPolicy: 'no-cache',
    skip: subprojectId === 0,
  })

  const [updateSubproject] = useMutation(UpdateSubprojectDocument, {
    extractorFn: (mutationData: UpdateSubprojectMutation) =>
      mutationData.projectManagementMutations.updateProjectmanagementSubproject,
    awaitRefetchQueries: true,
    refetchQueries: [SubprojectSidepanelDetailsDocument, SubprojectsDataCollectionDocument],
  })

  const getEmptyFormValues = (): SubprojectBasicDetails => ({
    name: undefined,
    startDate: undefined,
    dueDate: undefined,
    status: 'active',
    code: undefined,
    description: undefined,
  })

  const getFormValuesFromSubproject = (subproject: {
    name?: string | null
    startDate?: string | null
    dueDate?: string | null
    status?: string | null
    code?: string | null
    description?: string | null
  }): SubprojectBasicDetails => ({
    name: subproject.name || undefined,
    startDate: subproject.startDate || undefined,
    dueDate: subproject.dueDate || undefined,
    status: toStatusEnum(subproject.status),
    code: subproject.code || undefined,
    description: subproject.description || undefined,
  })

  const subprojectNode = data?.projectManagement?.subprojectsConnection?.nodes[0]
  const subprojectDetails = {
    subprojectName: subprojectNode?.name || undefined,
    subprojectId: subprojectId,
    description: subprojectNode?.description || undefined,
    status: toStatusEnum(subprojectNode?.status),
    code: subprojectNode?.code || undefined,
    startDate: subprojectNode?.startDate || undefined,
    dueDate: subprojectNode?.dueDate || undefined,
  }

  const form = useForm<SubprojectBasicDetails>({
    defaultValues: subprojectNode
      ? getFormValuesFromSubproject(subprojectNode)
      : getEmptyFormValues(),
    validate: (formData) => {
      const errors: FormErrors<SubprojectBasicDetails> = {}

      if (!formData.status) {
        errors.status = i18n.t('project_management.modals.error_messages.mandatory')
      }

      return errors
    },
    onSubmit: async (formData): Promise<Result<unknown>> => {
      const response = await updateSubproject({
        variables: {
          id: subprojectId,
          name: formData.name,
          startDate: formData.startDate,
          dueDate: formData.dueDate,
          status: formData.status,
          code: formData.code,
          description: formData.description,
        },
        awaitRefetchQueries: true,
      })
      const handled = handleResult(response, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }
      return handled
    },
  })

  useEffect(() => {
    form.setValues(getEmptyFormValues())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subprojectId])

  useEffect(() => {
    if (subprojectNode) {
      form.setValues(getFormValuesFromSubproject(subprojectNode))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleSave = () => {
    form.handleSubmit()
  }

  const handleFieldChanged = (
    fieldName: keyof SubprojectBasicDetails,
    fieldValue: string | undefined | boolean
  ) => {
    form.setValues({ ...form.values, [fieldName]: fieldValue })
  }

  return subprojectEntityFeatureEnabled ? (
    <OneModal isOpen={showSubprojectsSidepanelOne} onClose={onClose} position="right">
      <OneModal.Header title={subprojectData?.subprojectName || 'Subproject'} />
      <OneModal.Content>
        <StandardLayout>
          <Tabs
            key={subprojectId}
            activeTabId={activeTab}
            tabs={[
              {
                id: TABSIDS.HOURS,
                label: i18n.t('common.activity'),
                onClick: () => setActiveTab(TABSIDS.HOURS),
              },
              ...(subprojectId !== 0
                ? [
                    {
                      id: TABSIDS.SETTINGS,
                      label: i18n.t('common.settings'),
                      onClick: () => setActiveTab(TABSIDS.SETTINGS),
                    },
                  ]
                : []),
            ]}
          />

          {activeTab === TABSIDS.SETTINGS && (
            <SubprojectsSettingsTab
              subprojectId={subprojectId}
              subprojectData={subprojectData}
              formValues={form.values}
              formErrors={form.errors}
              onFieldChange={handleFieldChanged}
              subprojectDetails={subprojectDetails}
            />
          )}

          {activeTab === TABSIDS.HOURS && (
            // TO DO: Rename to activity tab once we get rid of the old sidepanel
            <SubprojectsPanel
              onClose={onClose}
              showSubprojectsPanel={showSubprojectsSidepanelOne}
              projectId={projectId}
              projectCurrency={projectCurrency}
              canSeeLaborCosts={canSeeLaborCosts}
              subprojectPanelData={subprojectData}
              subprojectEntityFeatureEnabled={subprojectEntityFeatureEnabled}
            />
          )}
        </StandardLayout>
      </OneModal.Content>

      <OneModal.Footer>
        {activeTab === TABSIDS.SETTINGS && (
          <Box flexDirection="row" gap="s12" justifyContent="flexEnd" width="full">
            <F0Button onClick={onClose} label={i18n.t('common.cancel')} variant="outline" />
            <F0Button
              onClick={handleSave}
              label={i18n.t('common.save')}
              disabled={loading || !form.isValid}
            />
          </Box>
        )}
      </OneModal.Footer>
    </OneModal>
  ) : (
    <SubprojectsPanel
      onClose={onClose}
      showSubprojectsPanel={showSubprojectsSidepanelOne}
      projectId={projectId}
      projectCurrency={projectCurrency}
      canSeeLaborCosts={canSeeLaborCosts}
      subprojectPanelData={subprojectData}
      subprojectEntityFeatureEnabled={subprojectEntityFeatureEnabled}
    />
  )
}
